import React, { useState, useEffect } from 'react';
import {
  Building2, TrendingUp, TrendingDown, Clock, Users, Target,
  AlertTriangle, BarChart3, Filter, Search, RefreshCw, Download,
  Calendar, PieChart, Activity, Star, Award, Timer, CheckCircle
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';

/** Mock AdminService department metrics must not be shown as production reporting (B3 / D-23). */
const PRODUCTION_DEPARTMENT_REPORT_ENABLED = false;

const DepartmentMetricsUnavailable = () => (
  <div className="p-6">
    <div className="max-w-2xl rounded-lg border border-amber-200 bg-amber-50 p-6 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" />
        <div>
          <h1 className="text-xl font-semibold text-foreground">Department Performance — not available</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Department Performance reporting (RPT-HD-08) is deferred pending product decision D-23 (department/team dimension and formulas).
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            This page does not display live data. No new reporting architecture has been introduced for this sprint.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const DepartmentMetricsLegacy = () => {
  const [metricsData, setMetricsData] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadDepartmentMetrics();
  }, [selectedPeriod, selectedDepartment]);

  const loadDepartmentMetrics = async () => {
    try {
      setLoading(true);
      const response = await new AdminService().getDepartmentMetrics(selectedPeriod, selectedDepartment);
      if (response.success) {
        setMetricsData(response.data);
        if (response.data.departments) {
          setDepartments(response.data.departments);
        }
      } else {
        setError(response.message || 'Failed to load Department Metrics');
      }
    } catch (err) {
      setError('Failed to load Department Metrics');
      console.error('Error loading Department Metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredDepartments = departments.filter(dept => {
    const matchesSearch = dept.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         dept.type?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <Activity className="h-4 w-4 text-gray-600" />;
  };

  const getPerformanceColor = (value, target, higherIsBetter = true) => {
    const percentage = (value / target) * 100;
    if (higherIsBetter) {
      if (percentage >= 100) return 'text-green-600';
      if (percentage >= 80) return 'text-yellow-600';
      return 'text-red-600';
    } else {
      if (percentage <= 100) return 'text-green-600';
      if (percentage <= 120) return 'text-yellow-600';
      return 'text-red-600';
    }
  };

  if (loading && !metricsData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
            <p className="text-gray-600">Loading Department Metrics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Department Metrics</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Track and analyze department performance metrics</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => {/* Export functionality */}}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button
            onClick={loadDepartmentMetrics}
            className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4 bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search departments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            />
          </div>
        </div>
        <select
          value={selectedDepartment}
          onChange={(e) => setSelectedDepartment(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
        >
          <option value="all">All Departments</option>
          {departments.map(dept => (
            <option key={dept.id} value={dept.id}>{dept.name}</option>
          ))}
        </select>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
        >
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="custom">Custom Range</option>
        </select>
      </div>

      {/* Overall Metrics */}
      {metricsData?.overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              {getTrendIcon(metricsData.overview.totalDepartments?.trend)}
            </div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Departments</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {metricsData.overview.totalDepartments?.value || 0}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {metricsData.overview.totalDepartments?.change || '0%'} from last period
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <Users className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              {getTrendIcon(metricsData.overview.totalMembers?.trend)}
            </div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Members</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {metricsData.overview.totalMembers?.value || 0}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {metricsData.overview.totalMembers?.change || '0%'} from last period
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <CheckCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              {getTrendIcon(metricsData.overview.avgResponseTime?.trend)}
            </div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Response Time</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {metricsData.overview.avgResponseTime?.value || '0h'}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {metricsData.overview.avgResponseTime?.change || '0%'} from last period
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Target className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              {getTrendIcon(metricsData.overview.slaCompliance?.trend)}
            </div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">SLA Compliance</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {metricsData.overview.slaCompliance?.value || '0'}%
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {metricsData.overview.slaCompliance?.change || '0%'} from last period
            </p>
          </div>
        </div>
      )}

      {/* Department Performance Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Department Performance</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Department</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Members</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tickets Resolved</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Avg Resolution</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Response Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Satisfaction</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">SLA Compliance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Performance Score</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredDepartments.map((dept) => (
                <tr key={dept.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{dept.name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{dept.type}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{dept.metrics?.members || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{dept.metrics?.ticketsResolved || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{dept.metrics?.avgResolutionTime || '0h'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{dept.metrics?.avgResponseTime || '0h'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-yellow-400 mr-1" />
                      <span className="text-sm text-gray-900 dark:text-gray-100">{dept.metrics?.satisfaction || '0.0'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getPerformanceColor(dept.metrics?.slaCompliance || 0, 95)}`}>
                      {dept.metrics?.slaCompliance || 0}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getTrendIcon(dept.metrics?.performanceTrend)}
                      <span className={`text-sm font-medium ml-1 ${getPerformanceColor(dept.metrics?.performanceScore || 0, 90)}`}>
                        {dept.metrics?.performanceScore || 0}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredDepartments.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No departments found</p>
          </div>
        )}
      </div>
    </div>
  );
};

const DepartmentMetrics = () => (
  PRODUCTION_DEPARTMENT_REPORT_ENABLED ? <DepartmentMetricsLegacy /> : <DepartmentMetricsUnavailable />
);

export default DepartmentMetrics;

